import type { User } from "@/types/auth";

import { isOwner } from "@/api/users";
import { useAuth } from "@/contexts/AuthContext";
import {
  hasElevatedAccessValue,
  usePrivilegedModeStore,
} from "@/utils/privilegedMode";

export interface MachineAppsRouteAuthLike {
  user?: {
    user?: { role?: User["role"] };
    roles?: string[] | null;
  } | null;
}

export interface MachineAppsRouteRedirect {
  to: "/dragon-tiles/$siteId";
  params: { siteId: string };
}

// Mirrors sphinx-gate's `requireOrgOwner`: platform staff pass unconditionally,
// otherwise the JWT must carry the `owner` org role. The `org-staff` slug is
// deliberately not honoured — sphinx-gate never checks it, and treating it as
// access would keep the page visible for staff who have switched the dev
// privileged-mode toggle off.
export function hasMachineAppsAccessValue(
  auth: MachineAppsRouteAuthLike | null | undefined,
  disabled: boolean,
): boolean {
  const jwt = auth?.user;
  return (
    hasElevatedAccessValue(jwt?.user?.role, disabled) ||
    isOwner(jwt?.roles ?? [])
  );
}

export function machineAppsRouteRedirect(
  auth: MachineAppsRouteAuthLike | null | undefined,
  disabled: boolean,
  siteId: string,
): MachineAppsRouteRedirect | null {
  if (hasMachineAppsAccessValue(auth, disabled)) return null;
  return { to: "/dragon-tiles/$siteId", params: { siteId } };
}

// Same rule as the route guard, evaluated reactively against the privileged
// mode store so the page revokes itself when the dev toggle flips.
export function useMachineAppsAccess(): boolean {
  const { user } = useAuth();
  const disabled = usePrivilegedModeStore((s) => s.disabled);
  return hasMachineAppsAccessValue({ user }, disabled);
}
